import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {
  DefaultMap,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';
import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {CHANGED} from 'external/dev_april_corgi+/js/emu/events';
import {IndeterminantCircular} from 'external/dev_april_corgi+/js/emu/progress';
import {Select} from 'external/dev_april_corgi+/js/emu/select';

import {Graph} from './execution/graph';
import {PlateProfile} from './execution/profiling_actions_handler';
import {OpentronsFlexRobot} from './execution/robots';
import {generateColorBlindStylePalette} from './types/colors';

import {fetchData} from './data';
import {formatVolume} from './node_element';
import {SimulationController, State} from './simulation_controller';

export function makeSimulateScriptDialog({
  graph,
}: {
  graph: Graph;
}): corgi.VElementOrPrimitive {
  return <SimulateScriptDialog graph={graph} />;
}

function SimulateScriptDialog(
  {graph}: {graph: Graph},
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  const robots: OpentronsFlexRobot[] = [
    {
      id: '0994f074-63b1-4aa9-aba1-e9e42a212dc9',
      name: 'simulator',
      model: 'opentrons_flex_with_staging_area',
      spec: 'http://no-such-robot.invalid',
      bays: {},
    },
  ];

  if (!state) {
    state = {
      currentUser: fetchData('petri.PetriService/GetCurrentUser', {}),
      error: undefined,
      profile: undefined,
      robot: robots[0],
      script: undefined,
      view: 'labware',
    };
  }
  const captured = state;

  let labware;
  if (state.error) {
    labware = [<div>{state.error}</div>];
  } else if (state.profile) {
    labware = [
      <div>
        {state.profile ? state.profile.tipProfile.pickedUp + ' tips' : ''}
      </div>,
    ];

    for (const [id, plate] of state.profile.plateProfiles) {
      labware.push(
        <div>
          <div className="font-bold mb-2 mt-6">{id}:</div>
          <LabwareView profile={plate} />
        </div>,
      );
    }
  } else {
    labware = [
      <div className="flex grow items-center justify-center w-full">
        <IndeterminantCircular />
      </div>,
    ];
  }

  let script;
  if (state.error) {
    script = (
      <textarea className="font-mono grow p-1 w-full">{state.error}</textarea>
    );
  } else if (state.script) {
    script = (
      <textarea className="font-mono grow p-1 text-sm w-full">
        {state.script}
      </textarea>
    );
  } else {
    script = (
      <div className="flex grow items-center justify-center w-full">
        <IndeterminantCircular />
      </div>
    );
  }
  return (
    <div
      className={
        'bg-neutral-800 flex flex-col min-h-[75vh] p-4 oy-4 rounded w-[48rem]'
      }
      js={corgi.bind({
        args: {graph, robots},
        controller: SimulationController,
        events: {render: 'wakeup'},
        state: [state, updateState],
      })}
    >
      <div className="flex">
        <div className="grow"></div>
        <Select
          unboundEvents={{corgi: [[CHANGED, 'changeView']]}}
          options={[
            {
              label: '▣ Labware',
              value: 'labware',
              selected: state.view === 'labware',
            },
            {
              label: '✎ Simulation code',
              value: 'code',
              selected: state.view === 'code',
            },
          ]}
        />
      </div>
      <div
        className={
          'flex flex-col grow' + (state.view === 'code' ? '' : ' hidden')
        }
      >
        <div>
          <Select
            unboundEvents={{corgi: [[CHANGED, 'selectRobot']]}}
            options={robots.map((r, i) => ({
              label: r.name,
              value: String(i),
              selected: captured.robot === r,
            }))}
          />
        </div>
        {script}
      </div>
      <div className={state.view === 'labware' ? '' : 'hidden'}>{labware}</div>
    </div>
  );
}

function LabwareView({profile}: {profile: PlateProfile}) {
  const styles = generateColorBlindStylePalette();
  const stylesByHash = new DefaultMap<string, string>(() =>
    checkExists(styles.pop()),
  );

  const wellsByPosition = new Map(
    [...profile.wells.values()].flatMap(w =>
      [...w.actuals.values()].map(a => [
        `${a.plate},${a.position.row},${a.position.col}`,
        {w, a},
      ]),
    ),
  );
  const unassigned = [...profile.wells.values()]
    .filter(w => w.actuals.size === 0 && w.maxVolumeNl > 0)
    .map(w => ({w, a: {loadedVolumeNl: 0, storedVolumeNl: 0}}));

  const views = [];
  for (let plateIndex = 0; plateIndex < profile.layouts.length; ++plateIndex) {
    const {columns, rows} = profile.layouts[plateIndex];
    const columnMajor = [];
    for (let column = 0; column < columns; ++column) {
      for (let row = 0; row < rows; ++row) {
        const key = `${plateIndex},${row},${column}`;
        const well = wellsByPosition.get(key) ?? unassigned.shift();
        const material =
          well?.w.materials.size === 1
            ? getOnlyElement(well.w.materials.values())
            : undefined;
        let selectable;
        let text;
        let style;
        if (well && well.a.loadedVolumeNl > 0 && material) {
          selectable = true;
          text = `Input ${formatVolume(well.a.loadedVolumeNl)} of ${material.label}`;
          style = stylesByHash.get(
            `Input ${formatVolume(well.a.loadedVolumeNl)} of ${material.id}`,
          );
        } else if (well && well.a.storedVolumeNl > 0 && material) {
          selectable = true;
          text = `Output ${formatVolume(well.a.storedVolumeNl)} of ${material.label}`;
          style = stylesByHash.get(
            `Output ${formatVolume(well.a.storedVolumeNl)} of ${material.id}`,
          );
        } else if (well) {
          selectable = false;
          text = 'Reaction well';
          style = `
            background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' height='50%' width='50%'><text dominant-baseline='central' text-anchor='middle' x='50%' y='50%' fill='%23ccc' font-size='16'>↣</text></svg>");
          `;
        }
        columnMajor.push(
          <label
            className={
              'block border border-1 border-neutral-600 group h-12 w-12 ' +
              (selectable ? 'cursor-pointer ' : '') +
              (text ? '' : 'pointer-events-none ') +
              'has-checked:before:absolute ' +
              'has-checked:before:border-4 ' +
              'has-checked:before:border-b-white ' +
              'has-checked:before:border-l-transparent ' +
              'has-checked:before:border-r-white ' +
              'has-checked:before:border-t-transparent ' +
              'has-checked:before:h-8 ' +
              'has-checked:before:rotate-45 ' +
              'has-checked:before:rounded-sm ' +
              'has-checked:before:translate-x-4 ' +
              'has-checked:before:translate-y-1 ' +
              'has-checked:before:w-4 '
            }
            style={style}
          >
            {selectable ? <input type="checkbox" className="hidden" /> : <></>}
            {text ? (
              <div
                className={
                  'absolute bg-neutral-700 hidden p-2 rounded translate-x-6 translate-y-6 ' +
                  'z-1 group-hover:block'
                }
              >
                {text}
              </div>
            ) : (
              <></>
            )}
          </label>,
        );
      }
    }

    const rowMajor = [];
    for (let row = 0; row < rows; ++row) {
      rowMajor.push(<div>{'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(row)]}</div>);
      for (let column = 0; column < columns; ++column) {
        rowMajor.push(columnMajor[column * rows + row]);
      }
    }
    views.push(
      <div
        className="gap-2 grid items-center justify-items-center"
        style={`grid-template-columns: 1rem repeat(${columns}, minmax(0, 1fr))`}
      >
        <div></div>
        {Array(columns)
          .fill(0)
          .map((_, i) => (
            <div>{i + 1}</div>
          ))}
        {rowMajor}
      </div>,
    );
  }
  return <div className="oy-4">{views}</div>;
}
